import { NgIf, NgFor, AsyncPipe, DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';

import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemIcon,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
  MatDivider,
} from '@angular/material/list';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { getObservations } from 'src/app/performance/store/selectors/performance.selector';

import * as performanceActions from '../../../performance/store/actions/performance.actions';
import { Departure, DepartureAllocation } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { DepartureItemDialogComponent } from '../departure-item-dialog/departure-item-dialog.component';

@Component({
  selector: 'app-departure-overview',
  templateUrl: './departure-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatNavList,
    NgFor,
    MatListItem,
    MatIcon,
    MatListItemIcon,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatIconButton,
    MatListItemMeta,
    MatDivider,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class DepartureOverviewComponent {
  readonly cycle$ = this.store.select(getSelectedCycle);
  readonly isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  readonly observations$ = this.store.select(getObservations);

  constructor(
    private readonly store: Store<RootState>,
    private readonly router: Router,
    @Inject(BreakpointServiceInjector)
    private readonly breakpoint: BreakpointService,
    private readonly dialog: DialogService,
    private readonly localeService: LocaleService,
  ) {
    this.cycle$
      .pipe(
        skipWhile((c) => c === undefined),
        take(1),
      )
      .subscribe((cycle) =>
        store.dispatch(performanceActions.loadObservationsForCycleRequest({ cycleId: cycle.id })),
      );
  }

  add() {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.router.navigate(['/cycle', cycle.id, 'departures', 'add']);
      this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
        if (isMobile) {
          this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
        }
      });
    });
  }

  public getTotalAnimals(departure: Departure) {
    return (
      departure?.allocations.reduce(
        (acc: number, section: DepartureAllocation) => acc + section.numberOfAnimals,
        0,
      ) ?? 0
    );
  }

  addDialog() {
    this.store.dispatch(
      cycleActions.setSelectedDepartureId({
        id: undefined,
      }),
    );
    this.cycle$
      .pipe(
        withLatestFrom(this.observations$),
        skipWhile(([c, _]) => !c?.id),
        take(1),
      )
      .subscribe(([cycle, observations]) => {
        this.dialog
          .form(DepartureItemDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.localeService.translate('add'),
            pushHistoryState: false,
            arrivalId: null,
            observations,
          })
          .subscribe((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              const nextDepartureId =
                cycle.departures && cycle.departures.length > 0
                  ? Math.max(...cycle.departures.map((d) => d.id)) + 1
                  : 1;
              this.store.dispatch(
                cycleActions.addDeparture({
                  cycleId: cycle.id,
                  departure: { ...result.result.departure, id: nextDepartureId },
                }),
              );
            }
          });
      });
  }

  openDepartureEdit(departureId: number) {
    this.store.dispatch(
      cycleActions.setSelectedDepartureId({
        id: departureId,
      }),
    );
    this.cycle$
      .pipe(withLatestFrom(this.observations$), take(1))
      .subscribe(([cycle, observations]) => {
        this.dialog
          .form(DepartureItemDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.localeService.translate('save'),
            pushHistoryState: false,
            departureId,
            observations,
          })
          .subscribe((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              this.store.dispatch(
                cycleActions.editDeparture({
                  cycleId: cycle.id,
                  departure: result.result.departure,
                }),
              );
            }
            this.store.dispatch(
              cycleActions.setSelectedDepartureId({
                id: undefined,
              }),
            );
          });
      });
  }

  delete(event: Event, departure: Departure) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      if (departure && departure.id) {
        this.store.dispatch(
          cycleActions.deleteDepartureWithConfirmation({ cycleId: cycle.id, id: departure.id }),
        );
      }
    });
  }

  sort(departures: Array<Departure>): Array<Departure> {
    return departures.slice().sort((a, b) => (a.departureDate < b.departureDate ? -1 : 0));
  }
}
